/**
*
*   Content Block
*       - A left/right content block + image or video
*
**/
'use client';

import Image from '@/components/image';
import { useEffect, useRef } from 'react';

//Build the Element
export default function Gallery({ product, ...props }){

	const section   = useRef();
	const wrap      = useRef();
	const video     = useRef();

	//Effect
	useEffect(()=>{

		//The scroll amount (height) to load in the bottle
		const scrollLoad = 600;

		//Sections + Elements
		const $section      = section.current;
		const $asset        = wrap.current;
		const $video        = Object.assign( video.current , { last: 0 });
		const $destination  = document.querySelector('[data-destination="1"]');
		const $ingredients  = document.getElementById('ingredients');
		const $gallery      = document.querySelector('#details .gallery');

		//Start & End Position
		const start         = 0;//$ingredients.getBoundingClientRect().y;
		const bottom        = $asset.getBoundingClientRect().bottom;

		//Video Seeking
		let seeking         = false;

		const Scroller = (()=>{

			//Calculates the Positioning
			const calculate = () => {

				//Recalcualte Element Position
				const offset        = Number( $destination?.dataset?.offset || 0 ) - ( Number( product.rendering.offset ) || 0 );//Number( $destination?.dataset?.offset || 0 ) + 50;
				const asset         = $asset.getBoundingClientRect();
				const destination   = $destination.getBoundingClientRect();
				const position      = $section.getBoundingClientRect();
				const locked        = $section.classList.contains('locked');

				//Get the Bottom Offset
				const assetBottom   = Number( ( asset.bottom + window.scrollY ).toFixed(0) );
				const finishLine    = Number( ( destination.bottom + window.scrollY + offset ).toFixed(0) );

				//Return all
				return { offset, asset, destination, position, locked, assetBottom , finishLine };

			};

			return {

				//The Positioning Interval
				intval: null,

				/**
				*
				*   video
				*       - Calculates the video frame and manage the video interval
				*
				**/
				video: (e) => {
					if( window.innerWidth >= 1100 ){

						//Using a Video Script
						if( $video.readyState == 4 ){

							//If we don't have an active positioning script, initalize it
							if( !Scroller.intval )
								Scroller.intval = window.setInterval( Scroller.positioning , 25 );

							//Get the Variables
							const { finishLine } = calculate();

							//Play the Video
							if( window.scrollY <= finishLine ){

								//Percentage Remaining
								const percentage = 1 - ( ( finishLine - window.scrollY ) / finishLine );

								//The next frame
								const frame = ( $video.duration * percentage ).toFixed(2);

								if( !seeking && ( $video.last != frame ) ){

									//Set the Video Time
									$video.currentTime = frame;

									$video.last = frame;
								}

							}

						}else
						//Using an Image Fallback
						if( $video.tagName == 'IMG' ){

							//If we don't have an active positioning script, initalize it
							if( !Scroller.intval )
								Scroller.intval = window.setInterval( Scroller.positioning , 25 );

						}
					}else{

						//Clear the Initialization Script
						if( Scroller.intval )
							Scroller.intval = window.clearInterval( Scroller.intval );

					}
				},

				/**
				*
				*   video
				*       - Calculates the video frame and manage the video interval
				*
				**/
				positioning: () => {

					//Load the Variables
					const { assetBottom, finishLine, position, asset, locked } = calculate();

					//Load in the bottle from bottom up
					if( window.scrollY <= scrollLoad )
						$asset.style.setProperty( '--position' ,  `${ ( ( scrollLoad - window.scrollY ) / scrollLoad ) * 100   }vh` );

					//Enforce 0vh if it's past the point
					if( window.scrollY >= scrollLoad && $video.style.getPropertyValue('--position') != '0vh' ){
						$asset.style.setProperty( '--position' , '0vh' );
					}

					/*
						//Less than the starting point, keep it locked in initial point
						if( window.scrollY <= start && !locked ){
							$section.removeAttribute('style');
							$section.classList.add('locked');
						}
					*/

					//On scroll complete, lock in place
					if( assetBottom >= finishLine && !locked ){
						$section.style.setProperty('top', `${ finishLine - asset.bottom   }px` );
						$section.style.setProperty('position' , 'absolute' , 'important');
						$section.classList.add('locked');
					}

					//During active scroll area, allow fixed
					if(
						(
							( window.scrollY >= start && assetBottom < finishLine ) ||
							( window.scrollY >= start && assetBottom >= finishLine && position.top >= 0 )
						)
						&& locked
					){
						$section.style.setProperty('position' , 'fixed' , 'important');
						$section.style.setProperty('top', '0' );
						$section.classList.remove('locked');
					}

				}

			};


		})();

		//Manage seeking / seeked state
		const VideoManager = {
			seeking:    () => seeking = true,
			seeked:     () => seeking = false
		};

		//The Events
		const Events = [ 'scroll' , 'resize' , 'focus' , 'blur' ];

		//Add Listeners
		$video.addEventListener( 'seeking' , VideoManager.seeking );
		$video.addEventListener( 'seeked' , VideoManager.seeked );

		//Set the Scale
		$asset.style.setProperty('--scale', product.rendering.scale );

		//Change type on scroll
		Events.forEach( $type => window.addEventListener( $type , Scroller.video ) );

		//Initiate
		window.dispatchEvent( new CustomEvent('scroll') );

		//Change type on scroll
		return () => {

			//Clear the Window Events
			Events.forEach( $type => window.removeEventListener( $type , Scroller.video ) );

			//Clear Video Seeking
			$video.removeEventListener( 'seeking' , VideoManager.seeking );
			$video.removeEventListener( 'seeked' , VideoManager.seeked );

			//Clear the Video Positioning, if it's running
			if( Scroller.intval )
				window.clearInterval( Scroller.intval );
		};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	},[]);

	//Return
	return (
		<section id="video" className="locked" ref={ section }>
			<div className="container grid">
				<div className="col"></div>
				<div className="col">
					<div ref={ wrap }>
						{(()=>{

							//Check for older than Safari 15 (No alpha transparency support)
							if( typeof navigator !== 'undefined' && navigator.userAgent.includes('Safari/') && navigator.userAgent.includes('Version/') ){
								const [first, second]   = [ ...navigator.userAgent.split('Version/')[1] ];
								const version           = first + second;

								if( +version <= 15 && product.rendering?.fallback?.src ) return (
									<Image ref={ video } src={ product.rendering?.fallback?.src } alt="" fill />
								);
							}

							//return Default Video
							return (
								<video ref={ video }>{
									product?.rendering?.video?.sort( ( f1 , f2 ) => {
										if( f1.mime_type.indexOf('mp4') > -1 ) return -1;
										if( f2.mime_type.indexOf('mp4') > -1 ) return 1;
										return 0;
									}).map( ( file , i ) => (
										<source key={ i } src={ file?.src } type={ file.mime_type.indexOf('mp4') > -1 ? `${ file.mime_type  };codecs=hvc1` : file.mime_type } />
									))
								}</video>
							);

						})()}
					</div>
				</div>
				<div className="col"></div>
				{/*
				<video autoplay="true">
					<source src="/assets/test/product.webm" type="video/webm" />
				</video>
				*/}
			</div>
		</section>
	);

}